import { ResourceList, UntranslatedResourceList, ResourceNavItem, UntranslatedResourceNavItem } from '../models/Navigation'
import { LanguageString } from '../models/Language'
import { translateResourceList, translateResourceNavItem } from '../helper/navigation'
import getSubdomain from '../utils/getSubdomain'

const medicareEnv = process.env.REACT_APP_MEDICARE_ENV || ''
const mymedicareEnv = process.env.REACT_APP_MY_MEDICARE_ENV || ''
const accountMyMedicareEnv = process.env.REACT_APP_ACCOUNT_MYMEDICARE_ENV || ''

const medicareSubdomain = getSubdomain(medicareEnv)
const mymedicareSubdomain = getSubdomain(mymedicareEnv)
const accountMyMedicareSubdomain = getSubdomain(accountMyMedicareEnv)
const esMedicareSubdomain = getSubdomain(medicareEnv, 'www')

export const resourceNav: (opts: { flags?: {[key: string]: boolean}}) => UntranslatedResourceNavItem[] = ({ flags }) => ([
  {
    id: 'resource-nav-1',
    navSection: 'basics',
    labels: {
      en: 'Basics',
      es: 'Lo Esencial'
    },
    subsections: [
      {
        id: 'basics-section-1',
        labels: {
          en: 'Learn',
          es: 'Aprenda'
        },
        links: [
          {
            id: '1',
            labels: {
              en: 'Get started with Medicare',
              es: 'Comenzar con Medicare'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/basics/get-started-with-medicare`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/basics/get-started-with-medicare`
            }
          },
          {
            id: '2',
            labels: {
              en: 'Medicare costs',
              es: 'Costos de Medicare'
            },
            hrefs: flags?.chHeaderUpdates081022
              ? {
                  en: `https://${medicareSubdomain}medicare.gov/basics/costs`,
                  es: `https://es.${esMedicareSubdomain}medicare.gov/basics/costs`
                }
              : {
                  en: `https://${medicareSubdomain}medicare.gov/your-medicare-costs`,
                  es: `https://es.${esMedicareSubdomain}medicare.gov/your-medicare-costs`
                }
          },
          {
            id: '3',
            labels: {
              en: 'Your Medicare rights',
              es: 'Sus derechos en Medicare'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/basics/your-medicare-rights`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/basics/your-medicare-rights`
            }
          },
          {
            id: '4',
            labels: {
              en: 'Reporting fraud & abuse',
              es: 'Reporte el fraude y abuso'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/basics/reporting-medicare-fraud-and-abuse`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/basics/reporting-medicare-fraud-and-abuse`
            }
          },
          {
            id: '5',
            labels: {
              en: 'End-Stage Renal Disease',
              es: 'Enfermedad Renal en Etapa Final'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/basics/end-stage-renal-disease`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/basics/end-stage-renal-disease`
            }
          }
        ]
      },
      {
        id: 'basics-section-2',
        labels: {
          en: 'Resources',
          es: 'Recursos'
        },
        links: [
          {
            id: '1',
            labels: {
              en: 'Talk to someone',
              es: 'Hablar con alguien'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/talk-to-someone/`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/talk-to-someone/`
            }
          },
          {
            id: '2',
            labels: {
              en: 'Forms, publications, & mailings',
              es: 'Formularios, publicaciones y envíos postales'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/basics/forms-publications-mailings`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/basics/forms-publications-mailings`
            }
          },
          {
            id: '3',
            labels: {
              en: '“Medicare & You” handbook',
              es: 'Manual "Medicare y Usted"'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/medicare-and-you`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/medicare-and-you`
            }
          },
          {
            id: '4',
            labels: {
              en: 'Advance directives & long-term care',
              es: 'Instrucciones anticipadas y el cuidado a largo plazo'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/manage-your-health/`, // was medicare.gov/manage-your-health/advance-directives-long-term-care
              es: `https://es.${esMedicareSubdomain}medicare.gov/manage-your-health`
            }
          }
        ]
      }
    ]
  },
  {
    id: 'resource-nav-2',
    navSection: 'health & drug plans',
    labels: {
      en: 'Health & Drug Plans',
      es: 'Planes de Salud y Medicamentos'
    },
    subsections: [
      {
        id: 'plans-section-1',
        labels: {
          en: 'Find & Compare',
          es: 'Buscar y comparar' // google translated
        },
        links: [
          {
            id: '1',
            labels: {
              en: 'Find health & drug plans',
              es: 'Encuentre planes de salud y medicamentos'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/plan-compare/`,
              es: `https://${medicareSubdomain}medicare.gov/plan-compare/#/?lang=es`
            }
          },
          {
            id: '2',
            labels: {
              en: 'Compare coverage options',
              es: 'Compare las opciones de cobertura'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/medicarecoverageoptions/`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/medicarecoverageoptions/`
            }
          },
          {
            id: '3',
            labels: {
              en: 'Find a Medicare Supplement Insurance (Medigap) policy',
              es: 'Encuentre una póliza de Seguro Sumplementario de Medicare (Medigap)'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/medigap-supplemental-insurance-plans/`,
              es: `https://${medicareSubdomain}medicare.gov/medigap-supplemental-insurance-plans/#/m?lang=es`
            }
          }
        ]
      },
      {
        id: 'plans-section-2',
        labels: {
          en: 'Learn',
          es: 'Aprenda'
        },
        links: [
          {
            id: '1',
            labels: (
              flags?.chHeaderUpdates04132023
                ? {
                    en: 'Medicare health plans',
                    es: 'Planes de salud de Medicare'
                  }
                : {
                    en: 'Get started with health & drug plans',
                    es: 'Comenzar con los planes de salud y medicamentos'
                  }
            ),
            hrefs: (
              flags?.chHeaderUpdates04132023
                ? {
                    en: `https://${medicareSubdomain}medicare.gov/health-drug-plans/health-plans`,
                    es: `https://es.${esMedicareSubdomain}medicare.gov/health-drug-plans/health-plans`
                  }
                : {
                    en: `https://${medicareSubdomain}medicare.gov/sign-up-change-plans`,
                    es: `https://es.${esMedicareSubdomain}medicare.gov/sign-up-change-plans`
                  }
            )
          },
          {
            id: '2',
            labels: (flags?.chHeaderUpdates04272023
              ? {
                  en: 'Medicare Supplemental Insurance (Medigap)',
                  es: 'Seguro Suplementario de Medicare (Medigap)'
                }
              : {
                  en: 'What health plans cover',
                  es: 'Que cubren los planes de salud'
                }
            ),
            hrefs: (flags?.chHeaderUpdates04272023
              ? {
                  en: `https://${medicareSubdomain}medicare.gov/health-drug-plans/medigap`,
                  es: `https://es.${esMedicareSubdomain}medicare.gov/health-drug-plans/medigap`
                }
              : {
                  en: `https://${medicareSubdomain}medicare.gov/what-medicare-covers/what-medicare-health-plans-cover`,
                  es: `https://es.${esMedicareSubdomain}medicare.gov/what-medicare-covers/what-medicare-health-plans-cover`
                }
            )
          },
          {
            id: '3',
            labels: {
              en: 'How plans work with other coverage',
              es: 'Cómo funcionan los planes con otra cobertura'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/supplements-other-insurance`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/supplements-other-insurance`
            }
          },
          {
            id: '4',
            labels: {
              en: 'Drug coverage (Part D)',
              es: 'Cobertura de medicamentos (Parte D)'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/drug-coverage-part-d`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/drug-coverage-part-d`
            }
          }
        ]
      },
      {
        id: 'plans-section-3',
        labels: {
          en: 'My Information',
          es: 'Mi Información'
        },
        descriptions: {
          en: 'Looking for your health & drug plan information?',
          es: '¿Busca información sobre su plan de salud y medicamentos?'
        },
        unauthedOnly: true,
        links: [
          {
            id: '1',
            labels: {
              en: 'Log in/create an account',
              es: 'Iniciar sesión/crear una cuenta'
            },
            hrefs: {
              en: `https://${accountMyMedicareSubdomain}medicare.gov/account/login`,
              es: `https://${accountMyMedicareSubdomain}medicare.gov/account/login?Lang=es-mx`
            }
          }
        ]
      }
    ]
  },
  {
    id: 'resource-nav-3',
    navSection: 'providers & services',
    labels: {
      en: 'Providers & Services',
      es: 'Proveedores y Servicios'
    },
    subsections: [
      {
        id: 'providers-section-1',
        labels: {
          en: 'Find & Compare',
          es: 'Buscar y comparar' // google translated
        },
        links: [
          {
            id: '1',
            labels: {
              en: 'Find care providers',
              es: 'Buscar proveedores de atención'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/care-compare/`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/care-compare/`
            }
          },
          {
            id: '2',
            labels: {
              en: 'Find medical equipment & suppliers',
              es: 'Encontrar equipos médicos y proveedores'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/medical-equipment-suppliers/`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/medical-equipment-suppliers/`
            }
          },
          {
            id: '3',
            labels: {
              en: 'Compare procedure costs',
              es: 'Comparar los costos del procedimiento'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/procedure-price-lookup/`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/procedure-price-lookup/`
            }
          }
        ]
      },
      {
        id: '2',
        labels: {
          en: 'Learn',
          es: 'Aprenda'
        },
        links: [
          {
            id: '1',
            labels: {
              en: 'What Medicare covers',
              es: 'Lo que cubre Medicare'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/what-medicare-covers`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/what-medicare-covers`
            }
          },
          {
            id: '2',
            labels: {
              en: 'Claims & appeals',
              es: 'Reclamaciones y apelaciones'
            },
            hrefs: {
              en: `https://${medicareSubdomain}medicare.gov/claims-appeals`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/claims-appeals`
            }
          }
        ]
      },
      {
        id: '3',
        labels: {
          en: 'My Information',
          es: 'Mi Información'
        },
        descriptions: {
          en: 'Looking for your providers & services information?',
          es: '¿Busca información sobre sus proveedores y servicios?'
        },
        unauthedOnly: true,
        links: [
          {
            id: '1',
            labels: {
              en: 'Log in/create an account',
              es: 'Iniciar sesión/crear una cuenta'
            },
            hrefs: {
              en: `https://${accountMyMedicareSubdomain}medicare.gov/account/login`,
              es: `https://${accountMyMedicareSubdomain}medicare.gov/account/login?Lang=es-mx`
            }
          }
        ]
      }
    ]
  }
]
)

export const accountNav: (opts: {claimsNewLink?: boolean, flags?: {[key: string]: boolean}}) => UntranslatedResourceList[] = ({ claimsNewLink, flags }) => ([

  {
    id: 'account-nav-1',
    labels: {
      en: 'My Account',
      es: 'Mi Cuenta'
    },
    links: [
      {
        id: '1',
        labels: {
          en: 'My account home',
          es: 'Iniciar mi cuenta'
        },
        hrefs: {
          en: flags?.useNewAccountHomeLinks ? `https://${mymedicareSubdomain}medicare.gov/my/home` : `https://${mymedicareSubdomain}medicare.gov/mbp/dashboard.aspx`,
          es: flags?.useNewAccountHomeLinks ? `https://es.${esMedicareSubdomain}medicare.gov/my/home` : `https://${mymedicareSubdomain}medicare.gov/mbp/dashboard.aspx?Lang=es-mx`
        }
      },
      {
        id: '2',
        labels: {
          en: 'My account settings',
          es: 'Configuración de mi cuenta'
        },
        hrefs: {
          en: flags?.accountSettingsChLinkUpdates ? `https://${mymedicareSubdomain}medicare.gov/my/account-settings` : `https://${mymedicareSubdomain}medicare.gov/mbp/myaccount.aspx`,
          es: flags?.accountSettingsChLinkUpdates ? `https://es.${esMedicareSubdomain}medicare.gov/my/account-settings` : `https://${mymedicareSubdomain}medicare.gov/mbp/myaccount.aspx?Lang=es-mx`
        }
      },
      {
        id: '3',
        labels: {
          en: 'Download my claims & personal data',
          es: 'Descargar mis reclamaciones y datos personales'
        },
        hrefs: {
          en: flags?.useNewDownloadMyData ? `https://${mymedicareSubdomain}medicare.gov/my/download-my-data` : `https://${mymedicareSubdomain}medicare.gov/mbp/DownloadMyData.aspx`,
          es: flags?.useNewDownloadMyData ? `https://es.${esMedicareSubdomain}medicare.gov/my/download-my-data` : `https://${mymedicareSubdomain}medicare.gov/mbp/DownloadMyData.aspx?Lang=es-mx`
        }
      }
    ]
  },
  {
    id: 'account-nav-2',
    labels: {
      en: 'My Information',
      es: 'Mi Información'
    },
    links: [
      {
        id: '1',
        labels: {
          en: 'My claims',
          es: 'Mis reclamaciones'
        },
        hrefs: claimsNewLink
          ? {
              en: `https://${mymedicareSubdomain}medicare.gov/my/claims`,
              es: `https://es.${esMedicareSubdomain}medicare.gov/my/claims`
            }
          : {
              en: `https://${mymedicareSubdomain}medicare.gov/mbp/claimsearchresult.aspx`,
              es: `https://${mymedicareSubdomain}medicare.gov/mbp/claimsearchresult.aspx?Lang=es-mx`
            }
      },
      {
        id: '2',
        labels: {
          en: 'My premiums',
          es: 'Mis primas'
        },
        hrefs: {
          en: flags?.useNewPremiumsPay ? `https://${mymedicareSubdomain}medicare.gov/my/premiums/dashboard` : `https://${mymedicareSubdomain}medicare.gov/mbp/PremiumInformation.aspx`,
          es: flags?.useNewPremiumsPay ? `https://es.${esMedicareSubdomain}medicare.gov/my/premiums/dashboard` : `https://${mymedicareSubdomain}medicare.gov/mbp/PremiumInformation.aspx?Lang=es-mx`
        }
      },
      {
        id: '3',
        labels: {
          en: 'My plans',
          es: 'Mis planes'
        },
        hrefs: {
          en: flags?.accountHomePlansFuture ? `https://${mymedicareSubdomain}medicare.gov/my/home` : `https://${mymedicareSubdomain}medicare.gov/mbp/plansandcoverage.aspx`,
          es: flags?.accountHomePlansFuture ? `https://es.${esMedicareSubdomain}medicare.gov/my/home` : `https://${mymedicareSubdomain}medicare.gov/mbp/plansandcoverage.aspx?Lang=es-mx`
        }
      },
      {
        id: '4',
        labels: {
          en: 'My preventive services',
          es: 'Mis servicios preventivos'
        },
        hrefs: {
          en: flags?.useNewPreventativeServicesLinking ? `https://${mymedicareSubdomain}medicare.gov/my/preventive-services` : `https://${mymedicareSubdomain}medicare.gov/mbp/mypreventiveservices.aspx`,
          es: flags?.useNewPreventativeServicesLinking ? `https://es.${esMedicareSubdomain}medicare.gov/my/preventive-services` : `https://${mymedicareSubdomain}medicare.gov/mbp/mypreventiveservices.aspx?Lang=es-mx`
        }
      },
      {
        id: '5',
        labels: {
          en: 'My providers',
          es: 'Mis proveedores'
        },
        hrefs: {
          en: flags?.chHeaderUpdates20220907 ? `https://${mymedicareSubdomain}medicare.gov/care-compare/my-providers` : `https://${mymedicareSubdomain}medicare.gov/mbp/myproviders.aspx`,
          es: flags?.chHeaderUpdates20220907 ? `https://${mymedicareSubdomain}medicare.gov/care-compare/my-providers?Lang=es-mx` : `https://${mymedicareSubdomain}medicare.gov/mbp/myproviders.aspx?Lang=es-mx`
        }
      },
      {
        id: '6',
        labels: {
          en: 'My saved drugs',
          es: 'Mis medicamentos guardados'
        },
        hrefs: {
          // Most recent ticket says to always send language for this url whether en or es
          en: `https://${mymedicareSubdomain}medicare.gov/plan-compare/mbp?target=PRESCRIPTIONS&lang=en`,
          es: `https://${mymedicareSubdomain}medicare.gov/plan-compare/mbp?target=PRESCRIPTIONS&lang=es`
        },
        isMbpRedirect: true
      },
      {
        id: '7',
        labels: {
          en: 'My saved pharmacies',
          es: 'Mis farmacias guardadas'
        },
        hrefs: {
          // Most recent ticket says to always send language for this url whether en or es
          en: `https://${mymedicareSubdomain}medicare.gov/plan-compare/mbp?target=PHARMACIES&lang=en`,
          es: `https://${mymedicareSubdomain}medicare.gov/plan-compare/mbp?target=PHARMACIES&lang=es`
        },
        isMbpRedirect: true
      }
    ]
  }
])

export type NavOptions = {
  flags?: {
    [flag: string]: boolean
  }
}
type AccountNavOptions = NavOptions & {
  claimsNewLink?: boolean
}

export const getAccountNav = (lang: LanguageString, opts: AccountNavOptions = {}): ResourceList[] => {
  return accountNav({ claimsNewLink: opts?.claimsNewLink, flags: opts?.flags }).map(list => translateResourceList(list, lang))
}

export const getResourceNavItems = (lang: LanguageString = 'en', opts: NavOptions = {}): ResourceNavItem[] => {
  return resourceNav({ flags: opts?.flags }).map(resourceNavItem => translateResourceNavItem(resourceNavItem, lang))
}
